"use client";

// AvatarNudge — the gentle post-login invitation to put a face on your cell.
// Shown once (dismissal persists locally) when the signed-in person has no
// uploaded avatar. Pure text in the Noticer's voice — no card, no border, no
// fill — floating just above the bottom edge. "add a photo" opens the picker
// right here and uploads inline; "later" lets it go.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

const DISMISS_KEY = "in-avatar-nudge";

export default function AvatarNudge() {
  const router = useRouter();
  const fileRef = useRef<HTMLInputElement>(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* private mode — show anyway */
    }
    const t = setTimeout(() => setShow(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* fine */
    }
    setShow(false);
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const res = await fetch("/api/me/avatar", {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      const json = (await res.json()) as { ok?: boolean };
      if (json.ok) {
        setDone(true);
        router.refresh();
        try {
          localStorage.setItem(DISMISS_KEY, "1");
        } catch {
          /* fine */
        }
        setTimeout(() => setShow(false), 2400);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "1.6rem",
        display: "flex",
        justifyContent: "center",
        zIndex: 30,
        pointerEvents: "none",
      }}
    >
      <p
        className="os-enter-soft"
        style={{
          margin: 0,
          fontSize: "0.88rem",
          color: "var(--ink-2)",
          pointerEvents: "auto",
          textWrap: "balance",
          textAlign: "center",
          padding: "0 1rem",
        }}
      >
        {done ? (
          <>there you are.</>
        ) : (
          <>
            put a face on your cell —{" "}
            <button className="quiet-link" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? "landing…" : "add a photo"}
            </button>{" "}
            <span style={{ color: "var(--ink-4)" }}>·</span>{" "}
            <button className="quiet-link" style={{ color: "var(--ink-3)" }} onClick={dismiss}>
              later
            </button>
          </>
        )}
      </p>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
